import * as THREE from "three";
import Experience from "../Experience";
import SpriteElement from "../World/SpriteElement";
import { channelJourney, linesToBillboardEls} from "../World/sets";
import CameraPathTracker from "../World/CameraPathTracker";
import LineElement from "../World/LineElement";
import { lineDark, secondaryColor } from "../Utils/styles";
import Plane from "../World/Plane";
import DataFetcher from "./arenafetcher";
import type Camera from "../Camera";
import type { ArenaBlock, ArenaImage } from "../types/arena";
import type { SpriteElementInput } from "../types/domain";
import type {
    RegeneratedLineData,
    SceneElementsToRegenerate,
    SceneWithUserData,
    UserDataObject,
} from "../types/scene";
import { debugLog } from "../Utils/debugLog";

type ImportedSceneData = {
    userData?: UserDataObject;
    [key: string]: unknown;
};
type RegenerationCarrier = {
    elementsToRegenerate?: SceneElementsToRegenerate;
    [key: string]: unknown;
};
type RegenRecord<T> = Record<string, T> | undefined;
type LineEndpoint = { x: number; y: number; z: number };
type RegenPlaneBlock = UserDataObject & {
    image?: ArenaImage | null;
    blockID?: string | number;
    connections?: unknown[];
    class?: string;
    base_class?: string;
    state?: string;
    id?: number;
    name?: string;
};
export default class ImportPath {
    experience: Experience;
    canvas: HTMLCanvasElement | null;
    scene: SceneWithUserData;
    camera: Camera;
    constructor()
    {
        this.experience = new Experience()
        this.canvas = this.experience.canvas
        this.scene = this.experience.scene as SceneWithUserData
        this.camera = this.experience.camera;
    }
    private isLineEndpoint(value: unknown): value is LineEndpoint {
        if (!value || typeof value !== "object") return false;
        const candidate = value as { x?: unknown; y?: unknown; z?: unknown };
        return (
            typeof candidate.x === "number" &&
            typeof candidate.y === "number" &&
            typeof candidate.z === "number"
        );
    }
    /** @param {string} input */
    loadGLTF (input: string): Promise<THREE.Object3D> {
        return new Promise((resolve, reject) => {
          const gltfLoader = this.experience?.resources?.loaders?.gltfLoader;
          if (!gltfLoader) {
            reject(new Error("GLTF loader unavailable"));
            return;
          }
          gltfLoader.load(
            input,
            (gltf: { scene: THREE.Object3D }) => {
              resolve(gltf.scene);
            },
            undefined,
            (error: unknown) => {
              reject(error);
            }
          );
        });
    };
    async insertLoadedScene (importedScene: ImportedSceneData | THREE.Object3D, sceneDocId: string)
    {
        if(importedScene?.userData){
            debugLog(sceneDocId, this.experience.sceneDocId);
            this.experience.sceneOperations.emptyScene(this.scene)
            this.scene.userData = importedScene.userData
            this.experience.uiHandlers?.showSceneOptions?.()
            if(this.experience.uiHandlers?.saveBtn && !this.experience.uiHandlers.saveBtn.classList.contains('active')){
                this.experience.uiHandlers.saveBtn.classList.add('active', 'disabled')
            }
            if(this.experience.uiHandlers?.restartAnywhereElseBtn?.classList.contains('active')){
                this.experience.uiHandlers.restartAnywhereElseBtn.classList.remove('active')
            }
            this.experience.uiHandlers?.menuRestart?.classList.remove("hidden")
            this.experience.uiHandlers?.wanderBtn?.parentElement?.classList.add("hidden")
            // this.experience.uiHandlers.pathwaysBtn.parentElement.classList.add("hidden")
            await this.experience.exportPath.duplicateScene(sceneDocId)
            debugLog(sceneDocId, this.experience.sceneDocId);
            await this.setRunningHeader()
            this.experience.uiHandlers?.setupPathUI?.()
            const pathline = await this.regenPathLines(this.scene.userData)
            const camline = await this.importCameraLine(this.scene.userData)
            this.scene.add(pathline)
            this.scene.add(camline)
    
            await this.regenCrossroadLines()
            await this.regenChannels()
            await this.regenTextBlocks()
            await this.regenPlaneBlocks()
            const boundingBox = new THREE.Box3()
            await this.experience.sceneOperations?.collectVisibleObjectsBoundingBox?.(boundingBox)
            await this.experience.sceneOperations.fitToBoxWithPadding(boundingBox, "mesh")
            this.experience.cameraPathTracker = new CameraPathTracker(true)
            this.experience.sceneOperations.autorotate = true
            this.experience.controls.firstClick = false
        }
    }
    async findMissingLabelFromImport()
    {
        if(this.scene.children.length < 1) {
            return
        } else if (this.scene.children.length > 1) 
        {
            this.scene.traverse((child: THREE.Object3D) => {
                if(child.type === "Object3D" && child.children && child.children.length > 0)
                {
                    child.children.forEach((nestedChild: THREE.Object3D) => {
                        if (nestedChild.userData && nestedChild.userData.blockType === "Channel") {
                            // Generate sprite for this channel
                            debugLog(nestedChild.userData);
                            if(nestedChild.userData.channelData || nestedChild.userData.connection){
                                const channel = new SpriteElement(nestedChild.userData.channelData)
                                debugLog(channel);
                            } 
                            // const spriteText = new SpriteText(nestedChild.userData.label);
                            // spriteText.position.set(nestedChild.position.x, nestedChild.position.y + 0.5, nestedChild.position.z); // Adjust Y position as needed
                            // scene.add(spriteText);
                        }
                    })
                }
            });
        }
    }
    async importCameraLine(data: RegenerationCarrier): Promise<THREE.Group>{
        const camLineGroup = new THREE.Group()
        camLineGroup.name = "camLineGroup"
        await this.forEachRegenEntry(data?.elementsToRegenerate?.cameraLines, async (line) => {
            const points = line.points ?? [];
            const camLine = await new CameraPathTracker(false, points).updateLine();
            if (camLine) camLineGroup.add(camLine)
        });
        return camLineGroup
    }
    async regenPathLines(data: RegenerationCarrier): Promise<THREE.Group> {
        const linePathGroup = new THREE.Group()
        linePathGroup.name = "pathLineGroup"
        await this.forEachRegenEntry(data?.elementsToRegenerate?.pathLines, async (line) => {
            if (!this.isLineEndpoint(line.sourceCenter) || !this.isLineEndpoint(line.targetCenter)) {
                return;
            }
            const linePath = new LineElement(line.sourceCenter, line.targetCenter, secondaryColor, line.isBetweenInteracted);
            if (linePath.line) {
                linePathGroup.add(linePath.line)
                linesToBillboardEls.add(linePath.line)
            }
        });
        return linePathGroup
    }
    /** @returns {Promise<void>} */
    async regenCrossroadLines(){
        await this.forEachRegenEntry(this.scene?.userData?.elementsToRegenerate?.crossroadLines, async (line) => {
            if (!this.isLineEndpoint(line.sourceCenter) || !this.isLineEndpoint(line.targetCenter)) {
                return;
            }
            const crossroadLine = new LineElement(line.sourceCenter, line.targetCenter, lineDark, line.isBetweenInteracted);
            linesToBillboardEls.add(crossroadLine.line)
        });
    }
    /** @returns {Promise<void>} */
    async regenChannels(){
        this.forEachRecord<UserDataObject>(this.scene?.userData?.elementsToRegenerate?.channels, (channel) => {
            void new SpriteElement(channel as SpriteElementInput);
        });
    }
    /** @returns {Promise<void>} */
    async regenTextBlocks(){
        this.forEachRecord<UserDataObject>(this.scene?.userData?.elementsToRegenerate?.textBlocks, (textBlock) => {
            void new SpriteElement(textBlock as SpriteElementInput);
        });
    }
    /** @returns {Promise<void>} */
    async regenPlaneBlocks(){
        await this.forEachRecord<RegenPlaneBlock>(this.scene?.userData?.elementsToRegenerate?.planeBlocks, async (planeBlock) => {
            if(!planeBlock?.image){
                const blockID = planeBlock.blockID;
                if (!blockID) return;
                const fetch = new DataFetcher(`/blocks/${String(blockID)}`)
                try {
                    const data = await fetch.fetchData();
                    if (!this.isArenaBlock(data)) return;
                    const fetchedBlock = data;
                    if(fetchedBlock.image){
                        planeBlock.image = fetchedBlock.image
                        planeBlock.connections = fetchedBlock.connections
                        planeBlock.class = fetchedBlock.class
                        planeBlock.base_class = fetchedBlock.base_class
                        planeBlock.state = fetchedBlock.state
                        planeBlock.id = fetchedBlock.id
                    }
                    planeBlock.name = "RegenMesh"
                } catch (error) {
                    // Private/deleted blocks can return 403/404; skip instead of aborting full import.
                    debugLog("[pathways] skip inaccessible plane block", blockID, error);
                    return;
                }
            }
            void new Plane({data: planeBlock});
        });
    }
    async setRunningHeader(){
        if (!this.experience.uiHandlers?.pathwayTitleHeader) return;
        this.experience.uiHandlers.pathwayTitleHeader.innerHTML = `<span class="pathway-fork">FORK</span>`
        this.experience.uiHandlers.pathwayTitleHeader.innerHTML += `
        from <span class="pathway-trailhead">${this.scene.userData.trailHead}</span>
             to <span class="pathway-trailend">${this.scene.userData.trailEnd}</span>
            `
        channelJourney.push(this.scene.userData.trailEnd)
    }
    
    
      
      
      
      // const root = gltf.scene;
      // scene.add(root);
      // console.log(gltf);
    private isArenaBlock(data: unknown): data is ArenaBlock {
        return Boolean(data && typeof data === "object" && "id" in data && "type" in data);
    }
    private forEachRecord<T>(
        entries: RegenRecord<T>,
        callback: (value: T) => Promise<void> | void
    ) {
        if (!entries || typeof entries !== "object") return Promise.resolve();
        return Promise.all(Object.values(entries).map((value) => callback(value)));
    }
    private forEachRegenEntry(
        entries: RegenRecord<RegeneratedLineData>,
        callback: (line: RegeneratedLineData) => Promise<void> | void
    ) {
        return this.forEachRecord(entries, callback);
    }
}
